import React from 'react';
import { useWorldStore } from '../state/worldStore';
import { authUser, edgeInset } from '../config';
import { line } from './RecordPlaques';

// PlaquePanel — move the studio's record frames.
//
// Lives on the ⚙ hub like every other tuning tool, because a placement editor
// you can only reach by knowing a query string is a tool nobody finds twice.
//
// ON-SCREEN BUTTONS, NOT JUST KEYS. The keyboard is faster once you know it, but
// the studio is an orbit room where the arrows are already doing camera work, so
// "I should be able to move them" has to be true with a mouse alone. The keys
// still work; these are the discoverable version of the same nudges.
const STEP = 0.05;
const FINE = 0.01;

export default function PlaquePanel() {
  const tool = useWorldStore((s) => s.adminTool);
  const edit = useWorldStore((s) => s.plaqueEdit);
  const setEdit = useWorldStore((s) => s.setPlaqueEdit);
  const groups = useWorldStore((s) => s.plaqueXf);
  const nudge = useWorldStore((s) => s.nudgePlaque);

  const { isAdmin } = authUser();
  if (tool !== 'plaques' || !isAdmin) return null;

  const group = edit.group;
  const sel = edit.i;
  const xf = groups[group] || [];
  const f = xf[sel];
  const r = (v) => Math.round(v * 100) / 100;

  const move = (dx, dy, dz, fine) => {
    if (!f) return;
    const k = fine ? FINE : STEP;
    nudge(group, sel, { pos: [f.pos[0] + dx * k, f.pos[1] + dy * k, f.pos[2] + dz * k] });
  };
  // Three axes: yaw hangs it on a wall, roll and tilt lay it down on a desk.
  const spin = (axis, d) => f && nudge(group, sel, { [axis]: (f[axis] || 0) + d });
  const size = (d) => f && nudge(group, sel, { size: Math.max(0.08, f.size + d) });

  const block = `${group === 'wall' ? 'plaques' : 'resultCards'}: [\n`
    + xf.map((v) => `      ${line(v, r)},`).join('\n') + '\n    ],';

  return (
    <div style={wrap}>
      <div style={{ font: '800 12px system-ui', letterSpacing: 0.6, marginBottom: 8 }}>RECORDS</div>

      {/* Which SURFACE. The wall holds released records; the result cards hold
          whatever just came out of the room. */}
      <div style={{ display: 'flex', gap: 5, marginBottom: 9 }}>
        {[['wall', 'Wall'], ['results', 'Results']].map(([g, label]) => (
          <button key={g} onClick={() => setEdit({ group: g, i: 0 })} style={{ ...chip(group === g), width: 'auto', flex: 1 }}>
            {label} <span style={{ opacity: 0.5 }}>{(groups[g] || []).length}</span>
          </button>
        ))}
      </div>

      {!xf.length ? (
        <div style={dim}>Walk into the Studio — the frames live in that room.</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 5, marginBottom: 9 }}>
            {xf.map((_, i) => (
              <button key={i} onClick={() => setEdit({ group, i })} style={chip(i === sel)}>{i + 1}</button>
            ))}
          </div>

          {/* X/Z are WORLD axes here, not camera-relative like the keyboard.
              A panel you look at while clicking can't also be steering by your
              gaze — predictable beats clever once the buttons are on screen. */}
          <Grid onMove={move} />

          <div style={{ display: 'flex', gap: 5, marginTop: 8 }}>
            <button style={btn} onClick={() => move(0, 1, 0)}>↑ up</button>
            <button style={btn} onClick={() => move(0, -1, 0)}>↓ down</button>
            <button style={btn} onClick={() => size(-0.02)}>−</button>
            <button style={btn} onClick={() => size(0.02)}>+</button>
          </div>

          <div style={{ ...dim, marginTop: 9, marginBottom: 4 }}>ROTATE</div>
          <div style={{ display: 'flex', gap: 5 }}>
            <button style={btn} onClick={() => spin('rotY', 0.08)}>↺ turn</button>
            <button style={btn} onClick={() => spin('rotY', -0.08)}>↻ turn</button>
          </div>
          <div style={{ display: 'flex', gap: 5, marginTop: 5 }}>
            <button style={btn} onClick={() => spin('rotZ', 0.08)}>⟲ roll</button>
            <button style={btn} onClick={() => spin('rotZ', -0.08)}>⟳ roll</button>
          </div>
          <div style={{ display: 'flex', gap: 5, marginTop: 5 }}>
            <button style={btn} onClick={() => spin('rotX', 0.08)}>⤒ tilt</button>
            <button style={btn} onClick={() => spin('rotX', -0.08)}>⤓ tilt</button>
          </div>

          {f && (
            <div style={{ ...dim, marginTop: 9, fontVariantNumeric: 'tabular-nums' }}>
              #{sel + 1} · [{f.pos.map(r).join(', ')}]<br />
              y {r(f.rotY || 0)} · z {r(f.rotZ || 0)} · x {r(f.rotX || 0)} · {r(f.size)}m
            </div>
          )}

          <button
            style={{ ...btn, width: '100%', marginTop: 9 }}
            onClick={() => { navigator.clipboard?.writeText(block); console.log(block); }}
          >Copy rooms.js line</button>
          <div style={{ ...dim, marginTop: 7, lineHeight: 1.5 }}>
            Auto-saved locally as you move. Paste the copied block over
            <code style={{ opacity: 0.8 }}> ROOMS.studio.{group === 'wall' ? 'plaques' : 'resultCards'}</code> to
            make it the default for everyone. Keys: <code>[ ]</code> turn · <code>, .</code> roll · <code>; '</code> tilt.
          </div>
        </>
      )}
    </div>
  );
}

// A d-pad reads faster than four labelled buttons for something spatial.
function Grid({ onMove }) {
  const cell = (label, dx, dy, dz, title) => (
    <button style={{ ...btn, padding: '7px 0' }} title={title} onClick={(e) => onMove(dx, dy, dz, e.shiftKey)}>{label}</button>
  );
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
      <span />{cell('▲', 0, 0, -1, 'away (−z)')}<span />
      {cell('◀', -1, 0, 0, 'left (−x)')}
      <span style={{ display: 'grid', placeItems: 'center', font: '600 9px system-ui', opacity: 0.4 }}>shift<br />= fine</span>
      {cell('▶', 1, 0, 0, 'right (+x)')}
      <span />{cell('▼', 0, 0, 1, 'toward (+z)')}<span />
    </div>
  );
}

const wrap = {
  position: 'fixed', left: edgeInset(16, 'left'), top: '84px', zIndex: 68,
  width: 'min(240px, calc(100vw - 32px))', maxHeight: '70vh', overflowY: 'auto',
  background: 'rgba(14,9,6,0.94)', border: '1px solid rgba(255,178,94,0.4)', borderRadius: 14,
  backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', color: '#ffe9c4',
  fontFamily: 'system-ui, sans-serif', padding: 12, pointerEvents: 'auto',
  boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
};
const btn = {
  flex: 1, padding: '7px 9px', borderRadius: 8, cursor: 'pointer',
  border: '1px solid rgba(255,178,94,0.45)', background: 'rgba(255,178,94,0.12)',
  color: '#ffe9c4', font: '700 11px system-ui', whiteSpace: 'nowrap',
};
const chip = (on) => ({
  ...btn, flex: '0 0 auto', width: 30,
  background: on ? 'rgba(255,178,94,0.35)' : 'rgba(255,178,94,0.1)',
  borderColor: on ? '#ffb25e' : 'rgba(255,178,94,0.3)',
});
const dim = { opacity: 0.55, font: '600 10.5px system-ui' };
